import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams, useParams, useNavigate } from 'react-router-dom';

import { brandText } from '../../config/branding';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { fetchAllStations } from '../../redux/slices/RevenueManagementLocationSlice';
import AutoCompleteSelectField from '../../shared-components/auto-complete-select-field/AutoCompleteSelectField';
// import PageTitle from '../../shared-components/page-title/PageTitle';
import MyButton from '../../shared-components/button/Button';
import CustomInput from '../../shared-components/custom-input/CustomInput';
import ConfirmBox from '../../shared-components/modal-box/ConfirmBox';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import RadioButton from '../../shared-components/radio-button/RadioButton';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotification';
import SidePaneModel from '../../shared-components/table-side-panel/SidePaneModel';
import { TooltipIcon } from '../../shared-components/tooltip/TooltipIcon';
import { getMyAccountCustomId, permissionCheck } from '../../shared-functions/JWTDocodeFunction';
import revenueApi, { EnergyCostType, UtilityCostPayload } from '../../utils/api/revenue';

import AccountRevenueSharing from './components/AccountRevenueSharing';
import ManagementAccountRevenue from './components/ManageAccountRevenue';
import ManagementStationRevenue from './components/ManageStationRevenue';
import UtilityCostDetails from './components/UtilityCostDetails';

export interface UtilityCostFormState {
  energyCostType: EnergyCostType;
  partnerRevenueSharePercentage: number | null;
  cpoRevenueSharePercentage: number | null;
  energyCostEnabled?: boolean;
}

const defaultUtilityCost: UtilityCostFormState = {
  energyCostType: 'none',
  partnerRevenueSharePercentage: null,
  cpoRevenueSharePercentage: null,
  energyCostEnabled: false,
};

/**
 * Helper to mark the revenue share as configured for the current tenant in localStorage
 * @param {string} [documentId] - The utility cost document ID
 * @returns {void}
 */
const markRevenueShareConfigured = (documentId?: string): void => {
  if (documentId) {
    localStorage.setItem(`revenue_tariff_configured_${documentId}`, 'true');
  }
  const accountId = getMyAccountCustomId();
  if (accountId) {
    localStorage.setItem(`revenue_tariff_configured_${accountId}`, 'true');
  }
};

/**
 * Helper to check if the revenue share was marked as configured for the current tenant
 * @param {string} [documentId] - The utility cost document ID
 * @returns {boolean} True if previously marked configured
 */
const isRevenueShareMarkedConfigured = (documentId?: string): boolean => {
  const byDocumentId = documentId
    ? localStorage.getItem(`revenue_tariff_configured_${documentId}`) === 'true'
    : false;
  const accountId = getMyAccountCustomId();
  const byAccount = accountId
    ? localStorage.getItem(`revenue_tariff_configured_${accountId}`) === 'true'
    : false;
  return byDocumentId || byAccount;
};

/**
 * User Management page function component
 * @returns {any} The rendered component
 */
function RevenueManagement() {
  const { t } = useTranslation('revenue');
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentPage: routePage } = useParams<{ currentPage?: string }>();
  const [searchParameters, setSearchParameters] = useSearchParams();
  const pageParameter = searchParameters.get('currentPage') || 'account';
  const [openTab, setOpenTab] = useState<number>();

  /**
   * Function to get initial page
   * @returns {string} The initial page
   */
  const getInitialPage = () => {
    if (routePage === 'partner') return 'account';
    if (routePage === 'station') return 'station';
    return pageParameter;
  };

  const [currentPage, setCurrentPage] = useState<string>(getInitialPage());

  /**
   * Function to set current page and update search parameters and route path
   * @param {any} value - The value
   */
  const handleSetCurrentPage = (value: React.SetStateAction<string>) => {
    const nextValue = typeof value === 'function' ? value(currentPage) : value;
    setCurrentPage(nextValue);

    // Sync search parameter for backward compatibility if needed
    setSearchParameters((previous) => {
      const updated = new URLSearchParams(previous.toString());
      updated.set('currentPage', nextValue);
      return updated;
    });

    // Also navigate to the matching route path
    const routeName = nextValue === 'account' ? 'partner' : 'station';
    const basePath = location.pathname.includes('revenue-sharing')
      ? '/ev-admin/revenue/revenue-sharing'
      : '/ev-admin/configuration/revenue-management';
    navigate(`${basePath}/${routeName}`, { replace: true });
  };

  // Sync route param with component state
  useEffect(() => {
    if (routePage === 'partner' && currentPage !== 'account') {
      setCurrentPage('account');
    } else if (routePage === 'station' && currentPage !== 'station') {
      setCurrentPage('station');
    } else if (!routePage) {
      // If we are at the base path, redirect to default '/partner' or current page
      const routeName = currentPage === 'account' ? 'partner' : 'station';
      const basePath = location.pathname.includes('revenue-sharing')
        ? '/ev-admin/revenue/revenue-sharing'
        : '/ev-admin/configuration/revenue-management';
      navigate(`${basePath}/${routeName}`, { replace: true });
    }
  }, [routePage, navigate, location.pathname, currentPage]);

  useEffect(() => {
    if (pageParameter && pageParameter !== currentPage && !routePage) {
      setCurrentPage(pageParameter);
    }
  }, [pageParameter, routePage]);

  const [selectedAccountId, setSelectedAccountId] = useState<string | number>('');

  const [utilityCost, setUtilityCost] = useState<UtilityCostFormState>(defaultUtilityCost);
  const [utilityCostCurrency, setUtilityCostCurrency] = useState<string>(
    brandText?.currency || 'SAR',
  );
  const [utilityCostLoading, setUtilityCostLoading] = useState(false);
  const [utilityCostSubmitting, setUtilityCostSubmitting] = useState(false);
  const [utilityCostConfirmBox, setUtilityCostConfirmBox] = useState(false);
  const [utilityCostSuccessModal, setUtilityCostSuccessModal] = useState(false);
  const [tariffNotConfiguredModal, setTariffNotConfiguredModal] = useState(false);
  const [utilityCostId, setUtilityCostId] = useState<string>('');
  const [isCalculateRevenueOpen, setIsCalculateRevenueOpen] = useState(false);

  useEffect(() => {
    /**
     * Function to fetch utility cost defaults and seed the form state
     * @returns {Promise<void>} Resolves once the utility cost defaults are loaded
     */
    const fetchUtilityCost = async () => {
      setUtilityCostLoading(true);
      try {
        const response = await revenueApi.fetchUtilityCost();
        const documentId = response?.data?._id;
        if (documentId) {
          setUtilityCostId(documentId);
        }

        const fetchedType: EnergyCostType =
          response?.data?.energyCostType ||
          (response?.data?.energyCostEnabled ? 'partner' : 'none');

        const rawCpoPercentage =
          response?.data?.cpoRevenuePercentage !== undefined
            ? response?.data?.cpoRevenuePercentage
            : response?.data?.cpoRevenueSharePercentage;

        const isCpoEmpty =
          rawCpoPercentage === null ||
          rawCpoPercentage === undefined ||
          rawCpoPercentage === '' ||
          (typeof rawCpoPercentage === 'string' && rawCpoPercentage.trim() === '');

        const isAlreadyConfigured = isRevenueShareMarkedConfigured(documentId);

        const isNeverUpdated =
          Boolean(response?.data?.createdAt && response?.data?.updatedAt) &&
          Math.abs(
            new Date(response.data.updatedAt).getTime() -
              new Date(response.data.createdAt).getTime(),
          ) < 1000;

        const isUnconfigured =
          !isAlreadyConfigured &&
          (!response?.data || isCpoEmpty || (Number(rawCpoPercentage) === 0 && isNeverUpdated));

        if (isUnconfigured) {
          setTariffNotConfiguredModal(true);
        }

        const fetchedUtilityCost: UtilityCostFormState = {
          energyCostType: fetchedType,
          energyCostEnabled: fetchedType !== 'none',
          partnerRevenueSharePercentage: isUnconfigured
            ? null
            : (response?.data?.partnerRevenueSharePercentage ??
              response?.data?.partnerRevenuePercentage ??
              null),
          cpoRevenueSharePercentage: isUnconfigured
            ? null
            : (response?.data?.cpoRevenueSharePercentage ??
              response?.data?.cpoRevenuePercentage ??
              null),
        };
        setUtilityCost(fetchedUtilityCost);
        if (response?.data?.currency) {
          setUtilityCostCurrency(response.data.currency);
        }
      } catch (error: any) {
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 500,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      } finally {
        setUtilityCostLoading(false);
      }
    };

    fetchUtilityCost();
  }, []);

  /**
   * Function to handle utility cost energy cost type change (partner, cpo, none)
   * @param {string} name - Field name
   * @param {string | number} selectedValue - The selected radio option
   */
  const handleRadioUtilityCostChange = (name: string, selectedValue: string | number) => {
    const energyCostType = String(selectedValue) as EnergyCostType;
    setUtilityCost((previous) => ({
      ...previous,
      energyCostType,
      energyCostEnabled: energyCostType !== 'none',
    }));
  };

  /**
   * Function to update a utility cost default value. Partner and CPO
   * shares are kept balanced to always sum to 100 - editing one field
   * auto-fills the other with the remainder.
   * @param {string} name - The field name (partnerRevenueSharePercentage or cpoRevenueSharePercentage)
   * @param {string | number} value - The new field value
   */
  const handleChangeUtilityCostValue = (name: string, value: string | number) => {
    const numericValue = value === '' ? null : Number(value);
    const counterpartName =
      name === 'partnerRevenueSharePercentage'
        ? 'cpoRevenueSharePercentage'
        : 'partnerRevenueSharePercentage';
    const counterpartValue =
      numericValue === null ? null : Math.round((100 - numericValue) * 100) / 100;

    setUtilityCost((previous) => ({
      ...previous,
      [name]: numericValue,
      [counterpartName]: counterpartValue,
    }));
  };

  /**
   * Function to handle configuring tariff manually from the popup modal.
   * Closes the modal and keeps the revenue share percentage fields empty for manual input.
   * @returns {void}
   */
  const handleConfigureTariff = () => {
    setTariffNotConfiguredModal(false);
    setUtilityCost((previous) => ({
      ...previous,
      partnerRevenueSharePercentage: null,
      cpoRevenueSharePercentage: null,
    }));
  };

  /**
   * Function to set default revenue share values (Partner: 100%, CPO: 0%) from the popup modal.
   * Closes the modal, updates the form state, and persists the default values to the backend.
   * @returns {Promise<void>} Resolves once the default values are saved
   */
  const handleSetDefaultTariff = async () => {
    setTariffNotConfiguredModal(false);
    setUtilityCost((previous) => ({
      ...previous,
      partnerRevenueSharePercentage: 100,
      cpoRevenueSharePercentage: 0,
    }));
    markRevenueShareConfigured(utilityCostId);
    try {
      await revenueApi.updateUtilityCost({
        partnerRevenueSharePercentage: 100,
        cpoRevenueSharePercentage: 0,
        energyCostType: utilityCost.energyCostType,
      });
      setUtilityCostSuccessModal(true);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 500,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  /**
   * Function to save the utility cost defaults. Called after the user
   * confirms the action in the confirmation prompt.
   * @returns {Promise<void>} Resolves once the save request completes
   */
  const confirmSubmitUtilityCost = async () => {
    setUtilityCostConfirmBox(false);
    setUtilityCostSubmitting(true);
    try {
      const payload: UtilityCostPayload = {
        partnerRevenueSharePercentage: utilityCost.partnerRevenueSharePercentage,
        cpoRevenueSharePercentage: utilityCost.cpoRevenueSharePercentage,
        energyCostType: utilityCost.energyCostType,
      };
      await revenueApi.updateUtilityCost(payload);
      markRevenueShareConfigured(utilityCostId);
      setUtilityCostSuccessModal(true);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 500,
            message: error?.response?.data?.message || t('somethingWentWrong'),
          },
        }),
      );
    } finally {
      setUtilityCostSubmitting(false);
    }
  };

  /**
   * Function to open the confirmation prompt before saving the utility cost
   * defaults
   */
  const handleSubmitUtilityCost = () => {
    setUtilityCostConfirmBox(true);
  };

  const {
    accountList,
    page: revenueStationPage,
    limit,
  } = useAppSelector((state) => state.revenueLocation);

  /**
   * Function to change account Id filter
   * @param {string} name - The name
   * @param {any} onChangeValue - The value
   */
  const handleChangeAccountId = (name: string, onChangeValue: string | number) => {
    setSelectedAccountId(onChangeValue);
    dispatch(
      fetchAllStations({
        page: revenueStationPage,
        limit,
        accountCustomId: onChangeValue.toString(),
      }),
    );
  };

  useEffect(() => {
    if (location.pathname.includes('revenue-sharing')) {
      setOpenTab(0);
    } else {
      setOpenTab(1);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (searchParameters.get('accountCustomId') || searchParameters.get('stationId')) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('accountCustomId');
      parameters.delete('stationId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  return (
    <div className="revenue-management-wrapper">
      <ConfirmBox
        open={utilityCostConfirmBox}
        title={t('updateRevenueShare')}
        subTitle={t('updateRevenueShareConfirm')}
        conformSubmit={confirmSubmitUtilityCost}
        closeModal={() => setUtilityCostConfirmBox(false)}
        submitLabel={t('confirm')}
      />
      <ModalBox
        open={utilityCostSuccessModal}
        title=""
        header={false}
        notification
        closeModal={() => setUtilityCostSuccessModal(false)}
        content={
          <SuccessNotification
            title={t('revenueShareUpdatedSuccessfully')}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={() => setUtilityCostSuccessModal(false)}
          />
        }
      />
      <ModalBox
        open={tariffNotConfiguredModal}
        title={t('currentTariffNotConfigured', {
          defaultValue: 'Current tariff is not configured',
        })}
        header
        closeModal={() => setTariffNotConfiguredModal(false)}
        content={
          <div className="tariff-not-configured-modal-content">
            <p className="tariff-not-configured-modal-description">
              {t('currentTariffNotConfiguredDesc')}
            </p>
            <div className="tariff-not-configured-modal-actions">
              <MyButton
                label={t('configure', { defaultValue: 'Configure' })}
                buttonType="outline"
                onClickFunc={handleConfigureTariff}
              />
              <MyButton
                label={t('setDefault', { defaultValue: 'Set default' })}
                buttonType="primary"
                onClickFunc={handleSetDefaultTariff}
              />
            </div>
          </div>
        }
      />
      <SidePaneModel
        open={isCalculateRevenueOpen}
        title={t('calculateRevenue', { defaultValue: 'Calculate Revenue' })}
        closePanel={() => setIsCalculateRevenueOpen(false)}
        preventQueryClear
        width="540px"
        content={
          <UtilityCostDetails
            energyCostType={utilityCost.energyCostType}
            partnerPercentage={utilityCost.partnerRevenueSharePercentage}
            cpoPercentage={utilityCost.cpoRevenueSharePercentage}
            currency={utilityCostCurrency}
          />
        }
      />
      <div className="container revenue-body">
        {false && (
          <div className="revenue-toggle-wrap">
            <AutoCompleteSelectField
              name=""
              inputLabel=""
              selectedValue={selectedAccountId}
              placeholder={t('accountID')}
              onChangeValue={handleChangeAccountId}
              options={accountList}
            />
          </div>
        )}
        <div className="table-content">
          {openTab === 0 && (
            <div className="table-content">
              {permissionCheck('cpoAccountRevenueSharingView') && <AccountRevenueSharing />}
            </div>
          )}
          {openTab === 1 && (
            <div className="table-content">
              <div className="table-content-top">
                <div className="utility-cost-left">
                  <div className="utility-cost-wrapper">
                    <p className="utility-cost-title">
                      {t('addUtilityProviderCost', { defaultValue: 'Add Utility Provider Cost' })}
                      <span>
                        <TooltipIcon
                          text={t('toggleToEnableUtilityCost', {
                            defaultValue: 'Select who bears the utility provider cost',
                          })}
                          infoColor="info"
                        />
                      </span>
                    </p>
                    <div className="utility-cost-radios">
                      <RadioButton
                        name="energyCostType"
                        fontSize="medium"
                        value={utilityCost.energyCostType}
                        list={[
                          { label: t('partner', { defaultValue: 'Partner' }), value: 'partner' },
                          { label: t('cpo', { defaultValue: 'CPO' }), value: 'cpo' },
                          { label: t('none', { defaultValue: 'None' }), value: 'none' },
                        ]}
                        onChangeValue={handleRadioUtilityCostChange}
                        isDisabled={utilityCostLoading}
                      />
                    </div>
                  </div>
                  <div className="utility-cost-divider" />
                  <div className="utility-cost-inputs-row">
                    <CustomInput
                      inputLabel={t('partnerSharePercentage', {
                        defaultValue: 'Partner Share (%)',
                      })}
                      name="partnerRevenueSharePercentage"
                      type="number"
                      value={utilityCost.partnerRevenueSharePercentage}
                      onChangeValue={handleChangeUtilityCostValue}
                      placeholder={t('enterPartnerShare', { defaultValue: 'Enter partner share' })}
                      maxValue={100}
                      disabled={utilityCostLoading}
                      requiredTooltip
                      tooltipText={t('defaultUtilityCostAppliedForPartner', {
                        defaultValue: 'Default utility cost applied for the partner',
                      })}
                      decimalLimit={2}
                    />
                    <CustomInput
                      inputLabel={t('cpoSharePercentage', { defaultValue: 'CPO Share (%)' })}
                      name="cpoRevenueSharePercentage"
                      type="number"
                      value={utilityCost.cpoRevenueSharePercentage}
                      onChangeValue={handleChangeUtilityCostValue}
                      placeholder={t('enterCpoShare', { defaultValue: 'Enter cpo share' })}
                      maxValue={100}
                      disabled={utilityCostLoading}
                      requiredTooltip
                      tooltipText={t('defaultUtilityCostAppliedForCpo', {
                        defaultValue: 'Default utility cost applied for the CPO',
                      })}
                      decimalLimit={2}
                    />
                    <div className="utility-cost-actions">
                      <MyButton
                        label={t('submit', { defaultValue: 'Submit' })}
                        buttonType="primary"
                        onClickFunc={handleSubmitUtilityCost}
                        loading={utilityCostSubmitting}
                        disableButton={utilityCostSubmitting || utilityCostLoading}
                      />
                    </div>
                  </div>
                </div>
                <div className="utility-cost-right">
                  <MyButton
                    label={t('calculateRevenue', { defaultValue: 'Calculate Revenue' })}
                    buttonType="primary"
                    onClickFunc={() => setIsCalculateRevenueOpen(true)}
                  />
                </div>
              </div>
              {currentPage === 'account' && permissionCheck('cpoAccountRevenueView') && (
                <ManagementAccountRevenue
                  currentPage={currentPage}
                  setCurrentPage={handleSetCurrentPage}
                  utilityCost={utilityCost}
                />
              )}
              {currentPage === 'station' && permissionCheck('cpoStationRevenueView') && (
                <ManagementStationRevenue
                  currentPage={currentPage}
                  setCurrentPage={handleSetCurrentPage}
                  selectedAccountId={selectedAccountId}
                  utilityCost={utilityCost}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RevenueManagement;
